import React, { useEffect, useState } from 'react'
import {
  BundlePlanBoxSeparator,
  BundlePlanBoxStyled,
  BundlePlanBoxProduct,
  BundlePlanBoxProductRow,
  BundlePlanBoxTitle,
  BundlePlanBoxProductTitle,
  BundlePlanBoxDiscount,
  BundlePlanBoxBilledAnnually,
  BundlePlanCustomPricing,
  CollapsibleTrigger,
} from 'Features/pricing/components/BundlePlanBox/BundlePlanBox.styled'
import {
  BundlePlanBoxButtonAndPrice,
  BundlePlanBoxPriceRow,
  PlanBoxAmount,
  PlanBoxPeriod,
} from 'Features/pricing/components/PlanBox/PlanBox.styled'
import { Button } from '@clickcease/cc-client-components-lib'
import { useTranslation } from 'react-i18next'
import { PLAN_TYPES, PlanTypes } from 'Types/PlansTypes'
import { Plan } from 'Features/pricing/types/Plan'
import { CurrenciesTypes } from 'Types/Currencies'
import { getAmountWithCurrency } from 'Utils/currencyHelper'
import { CSS } from '@stitches/react'

import { useNavigate } from 'react-router-dom'
import { isMobile } from 'react-device-detect'
import { ScheduleCallModal } from 'Features/ScheduleCallModal/ScheduleCallModal'
import { showKiloOrMillion } from 'Utils/number'
import { botMitigationPlanSizes, paidMarketingPlanSizes } from 'Features/pricing/models/planSizes'
import { useGetPlan } from 'Features/pricing/hooks/useGetPlan'
import { PRODUCT_TYPES } from 'Types/ProductTypes'
import { usePaymentsFlowSplit } from 'Hooks/split/usePaymentsFlow'
import { useAddSubscription } from 'Features/checkout/api/addSubscription'
import { usePricingEndOfTrial } from 'Hooks/split/usePricingEndOfTrial'
import { useUserStore } from 'Stores/userStoreModuleFed'
import * as Collapsible from '@radix-ui/react-collapsible'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleDown, faAngleRight } from '@fortawesome/pro-light-svg-icons'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { MobileCheckOut } from './MobileCheckOut'

type BundlePlanBoxProps = {
  paidMarketingPlan: PlanTypes
  botMitigationPlan: PlanTypes
  paidMarketingVisits: number
  botMitigationVisits: number
  paidMarketingPlansDb: Array<Plan>
  botMitigationPlansDb: Array<Plan>
  currency: CurrenciesTypes
  css?: CSS
  reactivation?: boolean
  billingCycleMonths?: number
  source: string
}

export const BundlePlanBox = ({
  paidMarketingVisits,
  botMitigationVisits,
  paidMarketingPlan,
  botMitigationPlan,
  currency,
  paidMarketingPlansDb,
  botMitigationPlansDb,
  css,
  reactivation,
  billingCycleMonths,
  source,
}: BundlePlanBoxProps) => {
  const { t } = useTranslation('pricing')
  const [isOpen, setIsOpen] = React.useState<boolean>(isMobile ? false : true)
  const [modalToShow, setModalToShow] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [pmPlan, setPmPlan] = useState<Plan>()
  const [bmPlan, setBmPlan] = useState<Plan>()
  const navigate = useNavigate()
  const paymentFlow = usePaymentsFlowSplit()
  const addSubscription = useAddSubscription()
  const DISCOUNT = 0.85
  const { findPlan } = useGetPlan()
  const { isInstallmentsSubscription } = useUserStore()

  const pricingEndOfTrial = usePricingEndOfTrial()

  // for yearly subscription which should be the default payment it's billingCycleInMonths=12
  const billingCycleInMonths = 12
  const isFeatureEnabled = usePaymentsFlowSplit()
  const installmentsPlan = '12_INSTALLMENTS'

  const handleAddSubscription = (plans: Array<number>) => {
    setIsLoading(true)
    addSubscription.mutate(
      { packageIds: plans, reactivation },
      {
        onSuccess: () => {
          setTimeout(() => {
            navigate('/onboarding')
          }, 2000)
        },
        onError: () => {
          setIsLoading(false)
          console.log('error adding subscription')
        },
      }
    )
  }

  const handleClick = () => {
    if (isLetsTalk) {
      setModalToShow(true)
    } else {
      if (paymentFlow === 'NO_CC' && !pricingEndOfTrial) {
        handleAddSubscription([pmPlan?.id || 0, bmPlan?.id || 0])
      } else {
        navigate('/checkout/?bundle', {
          state: {
            paidMarketingPlan: pmPlan,
            botMitigationPlan: bmPlan,
            currency,
            reactivation,
            billingCycleMonths,
          },
        })
      }
    }
  }

  const getPriceBeforeDiscount = (): number => {
    return pmPlan && bmPlan ? (pmPlan.amount + bmPlan.amount) / 12 : 0
  }

  const isLetsTalk =
    paidMarketingPlan === PLAN_TYPES.ENTERPRISE ||
    botMitigationPlan === PLAN_TYPES.ENTERPRISE ||
    paidMarketingVisits > paidMarketingPlanSizes[4] ||
    botMitigationVisits > botMitigationPlanSizes[4]

  useEffect(() => {
    setPmPlan(
      findPlan(
        paidMarketingPlansDb,
        paidMarketingPlan,
        currency,
        PRODUCT_TYPES.PAID_MARKETING,
        paidMarketingVisits,
        billingCycleInMonths
      )
    )
    setBmPlan(
      findPlan(
        botMitigationPlansDb,
        botMitigationPlan,
        currency,
        PRODUCT_TYPES.BOT_ZAPPING,
        botMitigationVisits,
        billingCycleInMonths
      )
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paidMarketingPlansDb,
    botMitigationPlansDb,
    paidMarketingVisits,
    botMitigationVisits,
    paidMarketingPlan,
    botMitigationPlan,
  ])
  const ButtonCSS = {
    minWidth: isMobile ? '134px' : '168px',
    maxWidth: isMobile ? '134px' : '226px',
    height: '42px',
    maxHeight: '42px',
    marginTop: isMobile ? '2px' : '20px',
  }

  const priceWithDiscount = getPriceBeforeDiscount() * DISCOUNT
  const amountWithCurrency = getAmountWithCurrency(currency, priceWithDiscount).replace(
    /\B(?=(\d{3})+(?!\d))/g,
    ','
  )

  const priceBeforeDiscount = getPriceBeforeDiscount()
    .toFixed(0)
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  const ctaText = () => {
    if (reactivation) {
      return t('bundles.reactivate')
    } else if (pricingEndOfTrial && paymentFlow === 'NO_CC') {
      return t('bundles.stay_protected')
    } else {
      return t('bundles.start_free_trial')
    }
  }

  const planBoxBillingIntervalText: string = (() => {
    let text = ''
    if (isFeatureEnabled === installmentsPlan && location.pathname === '/pricing') {
      text = t('bundles.billed_annually_or_monthly')
    } else if (isInstallmentsSubscription) {
      text = t('bundles.billed_monthly')
    } else {
      text = t('bundles.billed_annually')
    }
    return text
  })()

  return (
    <>
      {!modalToShow && (
        <BundlePlanBoxStyled css={css}>
          <Collapsible.Root open={isMobile ? isOpen : true} onOpenChange={setIsOpen}>
            <CollapsibleTrigger>
              <BundlePlanBoxTitle>{t('bundles.your_plan')}</BundlePlanBoxTitle>
              {isMobile && (
                <FontAwesomeIcon
                  style={{ marginLeft: 'auto', paddingInline: '16px', width: '16px' }}
                  icon={(isOpen ? faAngleDown : faAngleRight) as IconProp}
                />
              )}
            </CollapsibleTrigger>
            <Collapsible.Content>
              <BundlePlanBoxProduct>
                <BundlePlanBoxProductTitle>{t('bundles.paid_marketing')}</BundlePlanBoxProductTitle>
                <BundlePlanBoxProductRow>
                  {isMobile
                    ? `${paidMarketingPlan},`
                    : t('bundles.plan_type', { plan: paidMarketingPlan })}
                </BundlePlanBoxProductRow>
                <BundlePlanBoxProductRow>
                  {t(isMobile ? 'bundles.mobile_yearly_visits' : 'bundles.yearly_visits', {
                    visits:
                      paidMarketingVisits > paidMarketingPlanSizes[4]
                        ? t('paid_marketing.million')
                        : showKiloOrMillion(paidMarketingVisits * 1000),
                  })}
                </BundlePlanBoxProductRow>
              </BundlePlanBoxProduct>
              <BundlePlanBoxProduct>
                <BundlePlanBoxProductTitle>{t('bundles.bot_mitigation')}</BundlePlanBoxProductTitle>
                <BundlePlanBoxProductRow>
                  {isMobile
                    ? `${botMitigationPlan},`
                    : t('bundles.plan_type', { plan: botMitigationPlan })}
                </BundlePlanBoxProductRow>
                <BundlePlanBoxProductRow>
                  {t(isMobile ? 'bundles.mobile_yearly_visits' : 'bundles.yearly_visits', {
                    visits:
                      botMitigationVisits > botMitigationPlanSizes[4]
                        ? t('bot_mitigation.million')
                        : showKiloOrMillion(botMitigationVisits * 1000),
                  })}
                </BundlePlanBoxProductRow>
              </BundlePlanBoxProduct>
            </Collapsible.Content>
          </Collapsible.Root>
          <BundlePlanBoxSeparator />
          {isMobile ? (
            <MobileCheckOut
              amountWithCurrency={amountWithCurrency}
              priceBeforeDiscount={priceBeforeDiscount}
              isLetsTalk={isLetsTalk}
              isLoading={isLoading}
              handleClick={handleClick}
            />
          ) : (
            <BundlePlanBoxButtonAndPrice>
              {!isLetsTalk ? (
                <>
                  <BundlePlanBoxPriceRow>
                    <PlanBoxAmount>{amountWithCurrency}</PlanBoxAmount>
                    <PlanBoxPeriod>/m</PlanBoxPeriod>
                  </BundlePlanBoxPriceRow>
                  <BundlePlanBoxDiscount>{t('bundles.discount')}</BundlePlanBoxDiscount>
                  <BundlePlanBoxBilledAnnually>
                    {planBoxBillingIntervalText}
                  </BundlePlanBoxBilledAnnually>
                </>
              ) : (
                <BundlePlanCustomPricing> {t('bundles.custom_pricing')}</BundlePlanCustomPricing>
              )}

              <Button
                loading={isLoading}
                disabled={isLoading}
                onClick={handleClick}
                css={ButtonCSS}
                text={isLetsTalk ? t('lets_talk') : ctaText()}
              />
            </BundlePlanBoxButtonAndPrice>
          )}
        </BundlePlanBoxStyled>
      )}
      <ScheduleCallModal
        onOpenChange={() => setModalToShow(false)}
        isOpen={modalToShow}
        source={source}
      />
    </>
  )
}
